import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { api, url } from "../../axios/api";
import { Modal, Form, InputGroup, Badge, Button } from "react-bootstrap";
import { 
  ArrowLeft, Search, Loader2, X, Package, User, Calendar, CheckCircle, Barcode, Delete, LogOut, FileText
} from "lucide-react";
import FaturaSucessoModal from "../../components/FaturaSucesso";

const EmitirDocumento = () => {
  const user = useSelector((state) => state.user.user);
  const empresaId = user?.empresa_id;
  const user_id = user?.id;
  const navigate = useNavigate();
  const barcodeInputRef = useRef(null);

  const getToday = () => new Date().toISOString().split('T')[0];
  const getTaxRate = (taxes) => {
    if (!taxes) return 0;
    try {
      const parsed = typeof taxes === "string" ? JSON.parse(taxes) : taxes;
      return parseFloat(parsed?.taxa || parsed?.rate || parsed?.percentagem || 0);
    } catch {
      return 0;
    }
  };
  const getFinalProductPrice = (produto) => {
    const base = parseFloat(produto?.preco || 0);
    return base * (1 + getTaxRate(produto?.taxes) / 100);
  };
  const safeText = (value) => String(value ?? "").toLowerCase();

  const [documentoData, setDocumentoData] = useState({
    empresa_id: empresaId,
    user_id,
    doc_type: "FR", 
    numero: "PROVISÓRIO", 
    numero_fatura_origem: "",
    cliente_id: null,
    data_emissao: getToday(),
    data_vencimento: getToday(),
    itens: [],
    metodo_pagamento: "Dinheiro",
    observacoes: "Emissão de documento comercial",
    total_liquido: 0,
    total_desconto: 0,
    total_iva: 0,
    total_geral: 0,
    retencao_fonte: 0, 
    total_retido: 0,   
    valor_pago: 0,
    troco: 0,
    estado: "Emitido"
  });

  const [showSucesso, setShowSucesso] = useState(false);
  const [lastFaturaId, setLastFaturaId] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [modalSelecao, setModalSelecao] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchProduto, setSearchProduto] = useState("");
  const [searchCliente, setSearchCliente] = useState("");
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [barcodeSearch, setBarcodeSearch] = useState("");
  const [showDocsHoje, setShowDocsHoje] = useState(false);
  const [documentosHoje, setDocumentosHoje] = useState([]);
  const [totaisCaixa, setTotaisCaixa] = useState({});
  const [loadingDocsHoje, setLoadingDocsHoje] = useState(false);

  // ESTADOS DO TECLADO VIRTUAL DE PREÇO
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [keyboardItemIdx, setKeyboardItemIdx] = useState(null);
  const [keyboardValue, setKeyboardValue] = useState("");

  // ESTADOS PARA CADASTRO DE CLIENTE NO MODAL
  const [searchingNif, setSearchingNif] = useState(false);
  const [clienteData, setClienteData] = useState({
    nome: "",
    nif: "",
    telefone: "",
    email: "",
    endereco: "",
    empresa_id: empresaId,
    user_id: user_id,
  });

  useEffect(() => {
    if (empresaId) {
      fetchData();
      setDocumentoData(prev => ({ ...prev, empresa_id: empresaId, user_id }));
    }
  }, [empresaId, user_id]);

  const fetchData = async () => {
    try {
      const [resCli, resProd] = await Promise.all([
        api.get(`/app/clientes/empresa/${empresaId}`),
        api.get(`/app/produtos/filter/?empresa_id=${empresaId}`)
      ]);
      setClientes(Array.isArray(resCli.data) ? resCli.data : (resCli.data?.data || []));
      setProdutos(Array.isArray(resProd.data) ? resProd.data : (resProd.data?.data || []));
    } catch (e) {
      console.error("Erro ao carregar dados", e);
    }
  };

  const handleBarcodeSearch = async (e) => {
    e.preventDefault();
    if (!barcodeSearch.trim()) return;

    try {
      const res = await api.get(`/app/produtos/barcode/${empresaId}/${barcodeSearch.trim()}`);
      if (res.data) {
        handleAddItem(res.data);
        setBarcodeSearch("");
        barcodeInputRef.current?.focus();
      } else {
        Swal.fire("Atenção", "Produto com este código de barras não foi encontrado.", "warning");
      }
    } catch (error) {
      console.error("Erro ao buscar por barcode", error);
      Swal.fire("Atenção", "Produto não encontrado ou erro na leitura do código.", "warning");
    }
  };

  const syncData = (itens, retPercentManual = null) => {
    let liq = 0;
    let tIva = 0;
    let tDesc = 0;
    let baseRetencao = 0;

    const updatedItens = itens.map(item => {
      const preco = parseFloat(item.preco_unitario) || 0;
      const qtd = parseFloat(item.qtd) || 0;
      
      const descPercent = parseFloat(item.desconto_percentagem || 0);
      let descValor = parseFloat(item.desconto_valor || 0);
      
      if (descPercent > 0 && descValor === 0) {
        descValor = (preco * qtd) * (descPercent / 100);
      }

      const totalItemLiquido = (preco * qtd) - descValor;
      const valorIvaItem = totalItemLiquido * (parseFloat(item.iva_percentagem || 0) / 100);
      const totalItemFinal = totalItemLiquido + valorIvaItem;

      liq += totalItemLiquido;
      tIva += valorIvaItem;
      tDesc += descValor;

      if (item.tipo !== "Produto") {
        baseRetencao += totalItemLiquido;
      }

      return {
        ...item,
        desconto_valor: descValor,
        valor_iva_item: valorIvaItem,
        total_item_liquido: totalItemLiquido,
        total_item_final: totalItemFinal
      };
    });

    let retPercent = retPercentManual !== null 
      ? parseFloat(retPercentManual) 
      : documentoData.retencao_fonte;

    if (baseRetencao > 20000) {
      retPercent = 6.5;
    }

    const vRetencao = baseRetencao * (retPercent / 100);
    const geral = (liq + tIva) - vRetencao;

    setDocumentoData(prev => ({
      ...prev,
      itens: updatedItens,
      total_liquido: liq,
      total_desconto: tDesc,
      total_iva: tIva,
      total_retido: vRetencao,
      retencao_fonte: retPercent,
      total_geral: geral,
      troco: Math.max(0, (parseFloat(prev.valor_pago) || 0) - geral)
    }));
  };

  const handleAddItem = (p) => {
    if (p.tipo === "Produto") {
      const estoqueDisponivel = parseFloat(p.estoque_atual || p.quantidade || 0);
      if (estoqueDisponivel <= 0) {
        return Swal.fire("Stock Insuficiente", `O produto "${p.nome}" não possui quantidade válida ou disponível em stock.`, "warning");
      }
    }

    if (documentoData.itens.find(i => i.produto_id === p.id)) return;
    
    let taxaIvaVal = 0;
    if (p.taxes) {
      try {
        const parsedTaxes = typeof p.taxes === "string" ? JSON.parse(p.taxes) : p.taxes;
        taxaIvaVal = parseFloat(parsedTaxes.taxa || 0);
      } catch (err) {
        console.error("Erro ao analisar o campo taxes do produto:", err);
      }
    }

    const novoItem = {
      produto_id: p.id,
      nome: p.nome,
      tipo: p.tipo,
      observacoes: "Item adicionado ao documento",
      qtd: 1,
      preco_unitario: parseFloat(p.preco || 0),
      pode_alterar_preco: !!p.preco_venda_atendimento,
      desconto_valor: 0,
      desconto_percentagem: 0,
      iva_percentagem: taxaIvaVal,
      valor_iva_item: 0,
      total_item_liquido: 0,
      total_item_final: 0,
      estoque_max: p.tipo === "Produto" ? parseFloat(p.estoque_atual || p.quantidade || 0) : Infinity
    };
    
    syncData([...documentoData.itens, novoItem]);
  };

  const handleQtyChange = (idx, val) => {
    const c = [...documentoData.itens];
    const novaQtd = parseFloat(val) || 0;

    if (c[idx].tipo === "Produto" && novaQtd > c[idx].estoque_max) {
      Swal.fire("Atenção", `Quantidade superior ao stock disponível (${c[idx].estoque_max}).`, "warning");
      c[idx].qtd = c[idx].estoque_max;
    } else if (novaQtd <= 0) {
      c[idx].qtd = 1;
    } else {
      c[idx].qtd = val;
    }
    syncData(c);
  };

  // FUNÇÕES DE MANIPULAÇÃO DO TECLADO VIRTUAL
  const handleOpenKeyboard = (idx, currentPrice) => {
    setKeyboardItemIdx(idx);
    setKeyboardValue(currentPrice ? currentPrice.toString() : "");
    setShowKeyboard(true);
  };

  const handleKeyPress = (key) => {
    if (key === "C") {
      setKeyboardValue("");
    } else if (key === "backspace") {
      setKeyboardValue(prev => prev.slice(0, -1));
    } else if (key === ".") {
      if (!keyboardValue.includes(".")) {
        setKeyboardValue(prev => (prev === "" ? "0." : prev + "."));
      }
    } else {
      if (keyboardValue === "0" && key === "0") return;
      setKeyboardValue(prev => (prev === "0" ? key : prev + key));
    }
  };

  const handleConfirmPrice = () => {
    const novoPreco = parseFloat(keyboardValue) || 0;
    if (novoPreco <= 0) {
      return Swal.fire("Aviso", "O preço de venda deve ser superior a 0 Kz.", "warning");
    }

    const c = [...documentoData.itens];
    c[keyboardItemIdx].preco_unitario = novoPreco;
    syncData(c);
    setShowKeyboard(false);
  };

  // CONSULTA NIF AGT ADAPTADA PARA O MODAL INTERNO DO PDV
  const handleSearchByNif = async () => {
    if (!clienteData.nif || clienteData.nif.trim() === "") {
      return Swal.fire("Informação", "Por favor, digite um NIF para pesquisar.", "info");
    }

    setSearchingNif(true);
    try {
      const response = await api.get(`/app/clientes/nif/${clienteData.nif.trim()}`);
      const infoAgt = response.data?.ObterContribuinte?.contribuinte || response.data;
      
      if (infoAgt) {
        setClienteData({
          ...clienteData,
          nome: infoAgt.nome || infoAgt.razonSocial || infoAgt.razao_social || "",
          endereco: infoAgt.domicilioFiscal || infoAgt.endereco || "",
          telefone: infoAgt.telefone || clienteData.telefone || "",
          email: infoAgt.email || clienteData.email || ""
        });
        
        Swal.fire({
          title: "Sucesso",
          text: "Dados sincronizados com a AGT!",
          icon: "success",
          timer: 1800,
          showConfirmButton: false
        });
      } else {
        Swal.fire("Informação", "Nenhum cliente encontrado com este NIF na AGT.", "info");
      }
    } catch (error) {
      console.error("Erro ao buscar NIF:", error);
      Swal.fire("Aviso", "Não foi possível buscar dados deste NIF automaticamente.", "warning");
    } finally {
      setSearchingNif(false);
    }
  };

  // SALVAR NOVO CLIENTE E EXECUTAR ASSOCIAÇÃO DIRETA NA COMPRA
  const handleSalvarNovoCliente = async () => {
    if (!clienteData.nome.trim()) {
      return Swal.fire("Atenção", "O nome do cliente é obrigatório.", "warning");
    }

    try {
      const response = await api.post("/app/clientes", {
        ...clienteData,
        empresa_id: empresaId,
        user_id: user_id
      });
      
      const novoClienteSalvo = response.data?.data || response.data;

      setClientes(prev => [...prev, novoClienteSalvo]);
      setSelectedCliente(novoClienteSalvo);
      setDocumentoData(prev => ({ ...prev, cliente_id: novoClienteSalvo.id }));

      Swal.fire("Sucesso", "Cliente registrado e associado com sucesso!", "success");
      setModalSelecao(false);
    } catch (error) {
      console.error("Erro ao registrar cliente:", error);
      Swal.fire("Erro", "Falha ao registrar novo cliente no servidor.", "error");
    }
  };

  const handleFinalizar = async () => {
    if (!documentoData.cliente_id || documentoData.itens.length === 0) {
      return Swal.fire("Atenção", "Preencha os dados obrigatórios (Cliente e Artigos).", "warning");
    }
    
    const precisaQuitarImediato = ["FR", "TV"].includes(documentoData.doc_type);

    if (precisaQuitarImediato && parseFloat(documentoData.valor_pago) < documentoData.total_geral) {
      return Swal.fire("Atenção", "O valor pago não pode ser inferior ao total do documento em pronto pagamento.", "error");
    }

    setIsProcessing(true);
    try {
      const res = await api.post("/app/documentos/", documentoData);
      setLastFaturaId(res.data.id);
      setShowSucesso(true);
      fetchData();
      
      setDocumentoData(prev => ({
        ...prev,
        cliente_id: null,
        itens: [],
        total_liquido: 0,
        total_desconto: 0,
        total_iva: 0,
        total_geral: 0,
        total_retido: 0,
        valor_pago: 0,
        troco: 0,
        data_emissao: getToday(),
        data_vencimento: getToday()
      }));
      setSelectedCliente(null);
    } catch (e) {
      console.error(e);
      Swal.fire("Erro", "Erro ao processar e salvar o documento fiscal.", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const fetchDocumentosHoje = async () => {
    if (!empresaId || !user_id) return;
    setLoadingDocsHoje(true);
    try {
      const res = await api.get(`/app/documentos/caixa-diario?empresa_id=${empresaId}&user_id=${user_id}&data=${getToday()}`);
      setDocumentosHoje(res.data?.data || []);
      setTotaisCaixa(res.data?.totais || {});
      setShowDocsHoje(true);
    } catch (error) {
      console.error("Erro ao carregar documentos do caixa:", error);
      Swal.fire("Erro", "Nao foi possivel carregar as faturas emitidas hoje.", "error");
    } finally {
      setLoadingDocsHoje(false);
    }
  };

  const handleAnularDocumento = async (doc) => {
    const result = await Swal.fire({
      title: "Anular documento?",
      text: `Deseja anular ${doc.numero}? Os produtos serao repostos no stock quando aplicavel.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, anular",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc2626"
    });
    if (!result.isConfirmed) return;

    try {
      await api.post(`/app/documentos/${doc.id}/anular`, {
        empresa_id: empresaId,
        user_id,
        motivo: "Anulado no painel do caixa"
      });
      Swal.fire("Anulado", "Documento anulado com sucesso.", "success");
      fetchDocumentosHoje();
      fetchData();
    } catch (error) {
      console.error("Erro ao anular documento:", error);
      Swal.fire("Erro", error.response?.data?.message || "Falha ao anular documento.", "error");
    }
  };

  const handleFechoDiarioPrint = async () => {
    const result = await Swal.fire({
      title: "Fecho Diario",
      text: "Escolha o formato para imprimir o fecho do caixa corrente.",
      icon: "question",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Ticket",
      denyButtonText: "A4",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#0078d4",
      denyButtonColor: "#111827"
    });

    const formato = result.isConfirmed ? "ticket" : result.isDenied ? "a4" : null;
    if (!formato) return;
    const resource = formato === "ticket" ? "fecho-diario-ticket" : "fecho-diario-a4";
    window.open(`${url}/index.php/api/prints/${resource}?empresa_id=${empresaId}&user_id=${user_id}&data=${getToday()}`, "_blank", "noopener,noreferrer");
  };

  const precisaQuitarImediato = ["FR", "TV"].includes(documentoData.doc_type);
  const isInvalidPayment = precisaQuitarImediato && parseFloat(documentoData.valor_pago) < documentoData.total_geral && documentoData.itens.length > 0;

  return (
    <div className="win10-billing">
      <style>{`
        .win10-billing { min-height: 100vh; height: 100vh; background: #f3f2f1; display: flex; flex-direction: column; font-family: 'Segoe UI', sans-serif; overflow: hidden; }
        .win-header { background: #fff; border-bottom: 1px solid #d1d1d1; padding: 10px 20px; display: flex; justify-content: space-between; align-items: center; gap: 12px; }
        .billing-workspace { flex: 1; display: flex; overflow: hidden; }
        .billing-main { flex: 1; padding: 1rem; display: flex; flex-direction: column; gap: 1rem; overflow: hidden; max-width: 1200px; min-width: 0; }
        .billing-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; justify-content: flex-end; }
        .catalog-tools { display: flex; gap: 8px; flex-wrap: wrap; justify-content: flex-end; }
        .catalog-tools .input-group { width: 220px; }
        .catalog-grid { padding: 1rem; overflow: auto; display: flex; flex-wrap: wrap; gap: 1rem; }
        .billing-table-wrap { flex: 1; overflow: auto; }
        .billing-items-table { min-width: 760px; }
        .win-card { background: #fff; border: 1px solid #d1d1d1; border-radius: 0; display: flex; flex-direction: column; }
        .win-title { background: #f9f9f9; padding: 8px 12px; font-size: 11px; font-weight: bold; color: #555; border-bottom: 1px solid #eee; text-transform: uppercase; }
        .win-input { border-radius: 2px !important; border: 1px solid #ccc !important; font-size: 13px; }
        .sidebar-total { width: 400px; background: #fff; border-left: 1px solid #d1d1d1; display: flex; flex-direction: column; overflow-y: auto; flex-shrink: 0; }
        .total-box { background: #0078d4; color: #fff; padding: 25px; text-align: right; border-radius: 4px; margin: 10px; }
        .product-item-card { width: 180px; min-height: 104px; border: 1px solid #d2d0ce; background: white; padding: 12px; border-radius: 0; cursor: pointer; transition: .1s; }
        .product-item-card:hover { border-color: #0078d4; box-shadow: inset 3px 0 0 #0078d4; background: #f8fbff; }
        
        .input-preco-disabled::-webkit-outer-spin-button,
        .input-preco-disabled::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .input-preco-disabled { -moz-appearance: textfield; background-color: #f8f9fa !important; color: #6c757d !important; border-color: #e9ecef !important; }
        
        .key-btn { height: 60px; font-size: 20px; font-weight: bold; border-radius: 4px; border: 1px solid #d1d1d1; background: #fdfdfd; color: #333; transition: all 0.1s ease; }
        .key-btn:active { background: #e5e5e5; transform: scale(0.97); }
        .key-btn-action { background: #f3f3f3; }
        .key-btn-confirm { background: #0078d4; color: white; border-color: #0078d4; }
        .key-btn-confirm:active { background: #005a9e; color: white; }
        @media (max-width: 1199px) {
          .win10-billing { height: auto; overflow: auto; }
          .billing-workspace { flex-direction: column; overflow: visible; }
          .billing-main { max-width: none; overflow: visible; }
          .sidebar-total { width: auto; border-left: 0; border-top: 1px solid #d1d1d1; overflow: visible; }
        }
        @media (max-width: 767px) {
          .win-header { align-items: flex-start; flex-direction: column; padding: 10px 12px; }
          .billing-actions { width: 100%; justify-content: flex-start; }
          .billing-actions .badge { max-width: 100%; white-space: normal; text-align: left; }
          .billing-main { padding: 0.75rem; gap: 0.75rem; }
          .win-title { align-items: stretch !important; flex-direction: column; gap: 8px; }
          .catalog-tools { width: 100%; justify-content: stretch; }
          .catalog-tools .input-group { width: 100%; }
          .catalog-grid { gap: 0.5rem; padding: 0.75rem; }
          .product-item-card { width: calc(50% - 0.25rem); min-width: 0; padding: 10px; }
          .sidebar-total { padding: 1rem !important; }
          .total-box { margin: 8px 0; padding: 18px; }
          .total-box h2 { font-size: 1.35rem; overflow-wrap: anywhere; }
        }
        @media (max-width: 420px) {
          .product-item-card { width: 100%; }
        }
      `}</style>

      <div className="win-header">
        <div className="d-flex align-items-center gap-3">
          {
            user.nivel === "Administrador" && (
              <>
                <Button variant="light" className="border rounded-1 p-2" onClick={() => navigate(-1)}><ArrowLeft size={18}/></Button>
              </>
            )
          }
          <div>
            <h5 className="m-0 fw-bold text-primary">POS</h5>
            <small className="text-muted fw-bold">Emissao de documento e venda rapida</small>
          </div>
        </div>
        <div className="billing-actions">
           <Badge bg="primary" className="p-2 me-1">OPERADOR: {user?.nome}</Badge>
           
           {/* Botões extras visíveis apenas para o nível "caixa" */}
           {user?.nivel === "Caixa" && (
             <>
               <Button 
                 variant="warning" 
                 size="sm" 
                 className="d-flex align-items-center gap-1 fw-bold rounded-1 px-3"
                  onClick={handleFechoDiarioPrint}
               >
                 <FileText size={15} /> Fecho Diário
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="d-flex align-items-center gap-1 fw-bold rounded-1 px-3"
                  onClick={fetchDocumentosHoje}
                  disabled={loadingDocsHoje}
                >
                  <FileText size={15} /> Faturas Hoje
                </Button>
                <Link 
                 to="/auth/logout" 
                 className="btn btn-danger btn-sm d-flex align-items-center gap-1 fw-bold rounded-1 px-3 text-white text-decoration-none"
               >
                 <LogOut size={15} /> Logout
               </Link>
             </>
           )}
        </div>
      </div>

      <div className="billing-workspace">
        <div className="billing-main">
          
          <div className="win-card p-3 shadow-sm border-0">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="small fw-bold text-muted">TIPO DE DOCUMENTO</label>
                <Form.Select className="win-input fw-bold text-primary" value={documentoData.doc_type} onChange={e => setDocumentoData({...documentoData, doc_type: e.target.value})}>
                  <option value="FR">Fatura Recibo (FR)</option>
                  <option value="FT">Fatura (FT)</option>
                  <option value="FA">Fatura Autofatura (FA)</option>
                  <option value="FP">Fatura Pró-Forma (FP)</option>
                  <option value="PP">Orçamento / Proforma (PP)</option>
                </Form.Select>
              </div>

              <div className="col-md-2">
                <label className="small fw-bold text-muted"><Calendar size={12}/> DATA EMISSÃO</label>
                <Form.Control 
                  type="date" 
                  className="win-input" 
                  value={documentoData.data_emissao} 
                  onChange={e => setDocumentoData({
                    ...documentoData, 
                    data_emissao: e.target.value,
                    data_vencimento: e.target.value 
                  })} 
                />
              </div>

              <div className="col-md-2">
                <label className="small fw-bold text-muted"><Calendar size={12}/> DATA VENCIMENTO</label>
                <Form.Control 
                  type="date" 
                  className="win-input" 
                  value={documentoData.data_vencimento} 
                  onChange={e => setDocumentoData({...documentoData, data_vencimento: e.target.value})} 
                />
              </div>

              <div className="col-md-2">
                <label className="small fw-bold text-muted">RETENÇÃO (%)</label>
                <Form.Select className="win-input" value={documentoData.retencao_fonte} onChange={e => syncData(documentoData.itens, e.target.value)} disabled={documentoData.total_liquido > 20000}>
                  <option value="0">0%</option>
                  <option value="6.5">6.5%</option>
                </Form.Select>
              </div>

              <div className="col-md-3">
                <label className="small fw-bold text-muted">FORMA DE PAGAMENTO</label>
                <Form.Select className="win-input" value={documentoData.metodo_pagamento} onChange={e => setDocumentoData({...documentoData, metodo_pagamento: e.target.value})}>
                  <option value="Dinheiro">Dinheiro</option>
                  <option value="TPA">TPA</option>
                  <option value="Transferência">Transferência</option>
                </Form.Select>
              </div>
            </div>
          </div>

          <div className="win-card flex-grow-0" style={{ maxHeight: '250px'}}>
            <div className="win-title d-flex justify-content-between align-items-center py-1">
              <span>Catalogo POS</span>
              
              <div className="catalog-tools">
                <form onSubmit={handleBarcodeSearch}>
                  <InputGroup size="sm">
                    <InputGroup.Text className="bg-light text-primary"><Barcode size={14}/></InputGroup.Text>
                    <Form.Control 
                      ref={barcodeInputRef}
                      className="win-input" 
                      placeholder="Leitor de Código de Barras..." 
                      value={barcodeSearch}
                      onChange={e => setBarcodeSearch(e.target.value)}
                    />
                  </InputGroup>
                </form>

                <InputGroup size="sm">
                  <InputGroup.Text className="bg-white"><Search size={12}/></InputGroup.Text>
                  <Form.Control className="win-input" placeholder="Filtrar por nome..." onChange={e => setSearchProduto(e.target.value)} />
                </InputGroup>
              </div>
            </div>
            
            <div className="catalog-grid">
                {produtos.filter(p => safeText(p?.nome).includes(safeText(searchProduto))).map(p => (
                  <div key={p.id} className="product-item-card shadow-sm" onClick={() => handleAddItem(p)}>
                    <Package size={20} className="text-primary mb-2"/>
                    <div className="fw-bold small text-truncate">{p.nome}</div>
                    <div className="text-muted" style={{ fontSize: '11px' }}>
                      {p.tipo === "Produto" ? `Stock: ${p.estoque_atual || p.quantidade || 0}` : "Serviço (Ilimitado)"}
                    </div>
                    <div className="text-primary fw-bold small">{getFinalProductPrice(p).toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kz</div>
                    <div className="text-muted" style={{ fontSize: '10px' }}>Taxes: {getTaxRate(p.taxes)}%</div>
                  </div>
                ))}
            </div>
          </div>

          <div className="win-card flex-grow-1 overflow-hidden shadow-sm">
            <div className="win-title">Artigos incluídos no Documento</div>
            <div className="billing-table-wrap">
              <table className="table table-sm m-0 billing-items-table">
                <thead className="sticky-top bg-white">
                  <tr className="small text-muted">
                    <th className="ps-3">DESCRIÇÃO DO ARTIGO / SERVIÇO</th>
                    <th width="90">QTD</th>
                    <th width="140">PREÇO UNIT.</th>
                    <th width="100">DESC.(Kz)</th>
                    <th width="70" className="text-center">TAXES</th>
                    <th width="140">TOTAL LÍQUIDO</th>
                    <th width="40"></th>
                  </tr>
                </thead>
                <tbody>
                  {documentoData.itens.map((item, idx) => (
                    <tr key={idx} className="align-middle border-bottom">
                      <td className="ps-3 fw-bold small">
                        {item.nome} 
                        <span className="d-block text-muted fw-normal" style={{ fontSize: '10px' }}>Tipo: {item.tipo}</span>
                      </td>
                      <td>
                        <input 
                          type="number" 
                          step="0.01"
                          className="win-input form-control form-control-sm" 
                          value={item.qtd} 
                          onChange={e => handleQtyChange(idx, e.target.value)} 
                        />
                      </td>
                      <td>
                        <input 
                          type="number" 
                          step="0.01"
                          className={`win-input form-control form-control-sm cursor-pointer ${!item.pode_alterar_preco ? 'input-preco-disabled' : 'text-primary fw-bold border-primary bg-white'}`} 
                          value={item.preco_unitario} 
                          readOnly 
                          onClick={() => item.pode_alterar_preco && handleOpenKeyboard(idx, item.preco_unitario)}
                          disabled={!item.pode_alterar_preco}
                        />
                      </td>
                      <td>
                        <input 
                          type="number" 
                          className="win-input form-control form-control-sm"
                          value={item.desconto_valor} 
                          onChange={e => { const c = [...documentoData.itens]; c[idx].desconto_valor = e.target.value; syncData(c); }} 
                        />
                      </td>
                      <td className="text-center"><Badge bg="light" className="text-dark border">{item.iva_percentagem}%</Badge></td>
                      <td className="fw-bold small">{item.total_item_final.toLocaleString('pt-AO')} Kz</td>
                      <td><X size={16} className="text-danger cursor-pointer" onClick={() => syncData(documentoData.itens.filter((_, i) => i !== idx))} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="sidebar-total p-4">
          <div className="d-flex align-items-center gap-2 mb-4">
             <User size={20} className="text-primary"/>
             <h6 className="m-0 fw-bold">Entidade / Cliente</h6>
          </div>
          
          <div className="bg-light p-3 rounded-3 mb-4 d-flex justify-content-between align-items-center border">
            <div>
              <small className="d-block text-muted fw-bold" style={{fontSize: '10px'}}>NOME COMPLETO / RAZÃO SOCIAL</small>
              <span className="fw-bold text-dark">{selectedCliente?.nome || "Consumidor Final"}</span>
              {selectedCliente?.nif && <small className="d-block text-muted">NIF: {selectedCliente.nif}</small>}
            </div>
            <Button variant="outline-primary" size="sm" onClick={() => setModalSelecao(true)}>
              {selectedCliente ? "Trocar" : "Selecionar"}
            </Button>
          </div>

          <div className="flex-grow-1">
             <div className="d-flex justify-content-between mb-2 small">
               <span>Total de Descontos:</span>
               <span className="fw-bold text-danger">-{documentoData.total_desconto.toFixed(2)} Kz</span>
             </div>
             <div className="d-flex justify-content-between mb-2 small">
               <span>Total Líquido:</span>
               <span className="fw-bold">{documentoData.total_liquido.toFixed(2)} Kz</span>
             </div>
             <div className="d-flex justify-content-between mb-2 small">
               <span>Taxes acumuladas:</span>
               <span className="fw-bold">{documentoData.total_iva.toFixed(2)} Kz</span>
             </div>
             <div className="d-flex justify-content-between mb-2 small text-danger">
               <span className="fw-bold">Retenção na Fonte AI:</span>
               <span className="fw-bold">-{documentoData.total_retido.toFixed(2)} Kz</span>
             </div>
          </div>

          <div className="total-box shadow">
            <small className="d-block opacity-75 fw-bold" style={{fontSize: '10px'}}>VALOR TOTAL A PAGAR</small>
            <h2 className="m-0 fw-bold">{documentoData.total_geral.toLocaleString('pt-AO', { minimumFractionDigits: 2 })} Kz</h2>
          </div>

          <div className="mt-4">
            <label className={`small fw-bold mb-2 ${isInvalidPayment ? 'text-danger' : 'text-muted'}`}>VALOR RECEBIDO À DINHEIRO / MULTICAIXA</label>
            <input 
              type="number" 
              className={`win-input form-control form-control-lg fw-bold mb-3 ${isInvalidPayment ? 'border-danger text-danger' : 'text-primary border-primary'}`} 
              value={documentoData.valor_pago} 
              onChange={e => setDocumentoData({...documentoData, valor_pago: e.target.value, troco: e.target.value - documentoData.total_geral})} 
            />
            
            {documentoData.troco >= 0 && !isInvalidPayment && documentoData.itens.length > 0 && (
              <div className="p-3 rounded-3 bg-success bg-opacity-10 text-success mb-3 text-center fw-bold border border-success">
                TROCO REVERSO: {documentoData.troco.toFixed(2)} Kz
              </div>
            )}

            <Button 
              className="w-100 py-3 fw-bold rounded-3 btn-primary d-flex align-items-center justify-content-center gap-2 shadow" 
              onClick={handleFinalizar} 
              disabled={isProcessing || isInvalidPayment}
              style={{ background: "#0078d4", borderColor: "#0078d4" }}
            >
              {isProcessing ? <Loader2 className="animate-spin" /> : <><CheckCircle size={20}/> FINALIZAR EMISSÃO FISCAL</>}
            </Button>
          </div>
        </div>
      </div>

      {/* MODAL DE SELECIONAR OU CADASTRAR NOVO CLIENTE COM ABAS */}
      <Modal show={modalSelecao} onHide={() => setModalSelecao(false)} size="lg" backdrop="static">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fs-5 fw-bold text-primary">Gerenciamento de Entidades / Clientes</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="p-3">
            <ul className="nav nav-tabs mb-3" id="clientTabs" role="tablist">
              <li className="nav-item" role="presentation">
                <button 
                  className="nav-link active fw-bold" 
                  id="select-tab" 
                  data-bs-toggle="tab" 
                  data-bs-target="#select-pane" 
                  type="button" 
                  role="tab"
                >
                  Selecionar Existente
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button 
                  className="nav-link fw-bold" 
                  id="register-tab" 
                  data-bs-toggle="tab" 
                  data-bs-target="#register-pane" 
                  type="button" 
                  role="tab"
                  onClick={() => setClienteData({
                    nome: "",
                    nif: "",
                    telefone: "",
                    email: "",
                    endereco: "",
                    empresa_id: empresaId,
                    user_id: user_id,
                  })}
                >
                  Cadastrar Novo Cliente
                </button>
              </li>
            </ul>

            <div className="tab-content" id="clientTabsContent">
              {/* ABA 1: SELECIONAR CLIENTE EXISTENTE */}
              <div className="tab-pane fade show active" id="select-pane" role="tabpanel" aria-labelledby="select-tab">
                <Form.Control 
                  placeholder="Pesquisar por NIF ou Nome do Cliente..." 
                  className="win-input mb-3" 
                  value={searchCliente}
                  onChange={e => setSearchCliente(e.target.value)} 
                />
                <div className="list-group list-group-flush border rounded" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {clientes.filter(c => safeText(c?.nome).includes(safeText(searchCliente)) || safeText(c?.nif).includes(safeText(searchCliente))).map(c => (
                    <button 
                      key={c.id} 
                      type="button"
                      className="list-group-item list-group-item-action d-flex justify-content-between align-items-center" 
                      onClick={() => { 
                        setSelectedCliente(c); 
                        setDocumentoData({...documentoData, cliente_id: c.id}); 
                        setModalSelecao(false); 
                      }}
                    >
                      <div>
                        <div className="fw-bold text-dark">{c.nome}</div>
                        <small className="text-muted">NIF: {c.nif || "Não Informado"} {c.telefone ? `| Tel: ${c.telefone}` : ""}</small>
                      </div>
                      <Badge bg="primary" pill>Associar</Badge>
                    </button>
                  ))}
                  {clientes.filter(c => safeText(c?.nome).includes(safeText(searchCliente)) || safeText(c?.nif).includes(safeText(searchCliente))).length === 0 && (
                    <div className="p-3 text-center text-muted small">Nenhum cliente encontrado.</div>
                  )}
                </div>
              </div>

              {/* ABA 2: CADASTRAR NOVO CLIENTE */}
              <div className="tab-pane fade" id="register-pane" role="tabpanel" aria-labelledby="register-tab">
                <div className="row g-3">
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label className="small fw-bold text-muted">NIF (Identificação Fiscal)</Form.Label>
                      <InputGroup size="sm">
                        <Form.Control 
                          type="text" 
                          className="win-input" 
                          placeholder="Ex: 5000..." 
                          value={clienteData.nif}
                          onChange={e => setClienteData({...clienteData, nif: e.target.value})}
                        />
                        <Button 
                          variant="outline-secondary" 
                          onClick={handleSearchByNif}
                          disabled={searchingNif}
                        >
                          {searchingNif ? "..." : <Search size={14} />} Sincronizar AGT
                        </Button>
                      </InputGroup>
                    </Form.Group>
                  </div>

                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label className="small fw-bold text-muted">Nome do Cliente / Razão Social *</Form.Label>
                      <Form.Control 
                        size="sm"
                        type="text" 
                        className="win-input fw-bold" 
                        value={clienteData.nome}
                        onChange={e => setClienteData({...clienteData, nome: e.target.value})}
                        required
                      />
                    </Form.Group>
                  </div>

                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label className="small fw-bold text-muted">Telefone de Contato</Form.Label>
                      <Form.Control 
                        size="sm"
                        type="text" 
                        className="win-input" 
                        value={clienteData.telefone}
                        onChange={e => setClienteData({...clienteData, telefone: e.target.value})}
                      />
                    </Form.Group>
                  </div>

                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label className="small fw-bold text-muted">E-mail</Form.Label>
                      <Form.Control 
                        size="sm"
                        type="email" 
                        className="win-input" 
                        value={clienteData.email}
                        onChange={e => setClienteData({...clienteData, email: e.target.value})}
                      />
                    </Form.Group>
                  </div>

                  <div className="col-12">
                    <Form.Group>
                      <Form.Label className="small fw-bold text-muted">Endereço / Domicílio Fiscal</Form.Label>
                      <Form.Control 
                        size="sm"
                        as="textarea" 
                        rows={2}
                        className="win-input" 
                        value={clienteData.endereco}
                        onChange={e => setClienteData({...clienteData, endereco: e.target.value})}
                      />
                    </Form.Group>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-top d-flex justify-content-end gap-2">
                  <Button variant="light" size="sm" className="border" onClick={() => setModalSelecao(false)}>
                    Cancelar
                  </Button>
                  <Button variant="primary" size="sm" onClick={handleSalvarNovoCliente}>
                    Salvar e Associar Cliente
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* MODAL DE KEYBOARD NUMÉRICO (ESTILO POS) */}
      <Modal show={showDocsHoje} onHide={() => setShowDocsHoje(false)} size="xl" >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fs-6 fw-bold text-primary">Faturas emitidas hoje</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-2 mb-3">
            {[
              ["Documentos", totaisCaixa.documentos || 0],
              ["Faturado", `${parseFloat(totaisCaixa.faturado || 0).toLocaleString("pt-AO", { minimumFractionDigits: 2 })} Kz`],
              ["Recebido", `${parseFloat(totaisCaixa.recebido || 0).toLocaleString("pt-AO", { minimumFractionDigits: 2 })} Kz`],
              ["Lucro Est.", `${parseFloat(totaisCaixa.lucro || 0).toLocaleString("pt-AO", { minimumFractionDigits: 2 })} Kz`],
            ].map(([label, value]) => (
              <div key={label} className="col-6 col-md-3">
                <div className="border bg-light p-2">
                  <small className="text-muted fw-bold d-block">{label}</small>
                  <span className="fw-bold">{value}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="table-responsive border">
            <table className="table table-sm align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Documento</th>
                  <th>Cliente</th>
                  <th>Pagamento</th>
                  <th>Estado</th>
                  <th className="text-end">Total</th>
                  <th className="text-end">Acao</th>
                </tr>
              </thead>
              <tbody>
                {documentosHoje.length > 0 ? documentosHoje.map((doc) => (
                  <tr key={doc.id}>
                    <td className="fw-bold">{doc.numero}</td>
                    <td>{doc.Cliente?.nome || "Consumidor Final"}</td>
                    <td>{doc.metodo_pagamento || "N/A"}</td>
                    <td>
                      <span className={`badge ${doc.estado === "Anulado" ? "bg-danger-subtle text-danger" : "bg-success-subtle text-success"}`}>
                        {doc.estado}
                      </span>
                    </td>
                    <td className="text-end fw-bold">{parseFloat(doc.total_geral || 0).toLocaleString("pt-AO", { minimumFractionDigits: 2 })} Kz</td>
                    <td className="text-end">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        disabled={doc.estado === "Anulado"}
                        onClick={() => handleAnularDocumento(doc)}
                      >
                        Anular
                      </Button>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="6" className="text-center text-muted py-4">Nenhuma fatura emitida hoje.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" className="border" onClick={() => setShowDocsHoje(false)}>Fechar</Button>
          <Button variant="primary" onClick={handleFechoDiarioPrint}>Imprimir Fecho</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showKeyboard} onHide={() => setShowKeyboard(false)}  size="md" backdrop="static" contentClassName="rounded-0 border-dark">
        <Modal.Header className="bg-light py-2">
          <Modal.Title className="fs-6 fw-bold text-uppercase text-dark">
            Alterar Preço de Venda do Item
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-white p-3">
          {keyboardItemIdx !== null && (
            <div className="mb-3 text-center border-bottom pb-2">
              <span className="text-muted d-block small fw-bold">ITEM SELECIONADO:</span>
              <h6 className="fw-bold text-primary m-0">{documentoData.itens[keyboardItemIdx]?.nome}</h6>
            </div>
          )}
          
          <div className="p-3 mb-3 bg-dark text-white rounded text-end shadow-inner" style={{ minHeight: '65px' }}>
            <div className="small text-muted text-uppercase fw-bold" style={{ fontSize: '10px' }}>Novo Preço Unitário</div>
            <h2 className="m-0 fw-bold tracking-wide">
              {keyboardValue === "" ? "0" : keyboardValue} <span className="fs-5 text-muted">Kz</span>
            </h2>
          </div>

          <div className="d-flex flex-column gap-2">
            <div className="row g-2">
              <div className="col-4"><button type="button" className="w-100 key-btn" onClick={() => handleKeyPress("1")}>1</button></div>
              <div className="col-4"><button type="button" className="w-100 key-btn" onClick={() => handleKeyPress("2")}>2</button></div>
              <div className="col-4"><button type="button" className="w-100 key-btn" onClick={() => handleKeyPress("3")}>3</button></div>
            </div>
            <div className="row g-2">
              <div className="col-4"><button type="button" className="w-100 key-btn" onClick={() => handleKeyPress("4")}>4</button></div>
              <div className="col-4"><button type="button" className="w-100 key-btn" onClick={() => handleKeyPress("5")}>5</button></div>
              <div className="col-4"><button type="button" className="w-100 key-btn" onClick={() => handleKeyPress("6")}>6</button></div>
            </div>
            <div className="row g-2">
              <div className="col-4"><button type="button" className="w-100 key-btn" onClick={() => handleKeyPress("7")}>7</button></div>
              <div className="col-4"><button type="button" className="w-100 key-btn" onClick={() => handleKeyPress("8")}>8</button></div>
              <div className="col-4"><button type="button" className="w-100 key-btn" onClick={() => handleKeyPress("9")}>9</button></div>
            </div>
            <div className="row g-2">
              <div className="col-4"><button type="button" className="w-100 key-btn key-btn-action text-danger" onClick={() => handleKeyPress("C")}>C</button></div>
              <div className="col-4"><button type="button" className="w-100 key-btn" onClick={() => handleKeyPress("0")}>0</button></div>
              <div className="col-4"><button type="button" className="w-100 key-btn key-btn-action" onClick={() => handleKeyPress(".")}>.</button></div>
            </div>
            <div className="row g-2">
              <div className="col-6">
                <button type="button" className="w-100 key-btn key-btn-action text-muted d-flex align-items-center justify-content-center" onClick={() => handleKeyPress("backspace")}>
                  <Delete size={20} />
                </button>
              </div>
              <div className="col-6">
                <button type="button" className="w-100 key-btn key-btn-confirm" onClick={handleConfirmPrice}>
                  Confirmar
                </button>
              </div>
            </div>
          </div>
          <div className="mt-3 d-grid">
            <button type="button" className="btn btn-sm btn-outline-secondary rounded-1 py-2 fw-bold text-uppercase" onClick={() => setShowKeyboard(false)}>
              Cancelar Operação
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {showSucesso && (
        <FaturaSucessoModal faturaId={lastFaturaId} onClose={() => setShowSucesso(false)} />
      )}
    </div>
  );
};

export default EmitirDocumento;
